import Phaser from 'phaser';
import { ENEMY_TYPES } from '../entities/EnemyTypes';
import { Upgrade, UpgradeSystem } from '../systems/UpgradeSystem';
import { DifficultySystem, DifficultyLevel, DIFFICULTY_CONFIGS } from '../systems/DifficultySystem';

export class GameScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemies!: Phaser.Physics.Arcade.Group;
  private projectiles!: Phaser.Physics.Arcade.Group;
  private xpOrbs!: Phaser.Physics.Arcade.Group;
  private sporeClouds!: Phaser.Physics.Arcade.Group;
  private wasd!: { [key: string]: Phaser.Input.Keyboard.Key };
  private upgradeSystem!: UpgradeSystem;
  private difficultySystem!: DifficultySystem;
  private currentDifficulty!: DifficultyLevel;

  private killCount: number = 0;
  private survivalTime: number = 0;
  private lastEnemySpawn: number = 0;
  private readonly enemySpawnRate: number = 2000; // base ms between spawns
  private gameStartTime: number = 0;
  private difficultyMultiplier: number = 1;
  private isGameOver: boolean = false;
  private isPaused: boolean = false;

  // Lives system for Recruit difficulty
  private lives: number = 1;
  private respawnInvincibilityTimer: number = 0;
  private isRespawning: boolean = false;

  private killCountText!: Phaser.GameObjects.Text;
  private survivalTimeText!: Phaser.GameObjects.Text;
  private hpText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private xpBarGraphics!: Phaser.GameObjects.Graphics;

  // Arena boundaries
  private readonly ARENA_WIDTH = 1000;
  private readonly ARENA_HEIGHT = 700;
  private readonly ARENA_OFFSET_X = 100;
  private readonly ARENA_OFFSET_Y = 50;

  // Cache for getGameState() — returned when scene is stopped/invalid
  private _gameStateCache: ReturnType<GameScene['_buildGameState']> | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: { difficulty?: DifficultyLevel }) {
    // Get difficulty from data or use saved/default
    this.difficultySystem = new DifficultySystem();
    this.currentDifficulty = data.difficulty || this.difficultySystem.getCurrentDifficulty();
    this.difficultySystem.setDifficulty(this.currentDifficulty);
  }

  create() {
    const { width, height } = this.cameras.main;
    this.gameStartTime = Date.now();
    this.killCount = 0;
    this.survivalTime = 0;
    this.difficultyMultiplier = 1;
    this.isGameOver = false;
    this.isRespawning = false;
    this.upgradeSystem = new UpgradeSystem();

    // Get difficulty config
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];
    this.lives = difficultyConfig.lives;

    // Arena background
    this.add.rectangle(
      this.ARENA_OFFSET_X + this.ARENA_WIDTH / 2,
      this.ARENA_OFFSET_Y + this.ARENA_HEIGHT / 2,
      this.ARENA_WIDTH,
      this.ARENA_HEIGHT,
      0x1a1a2e,
    ).setStrokeStyle(2, 0x444466);

    // Procedural textures (only generate once per texture key)
    this.buildTextures();

    // Player sprite
    this.player = this.physics.add.sprite(width / 2, height / 2, 'player');
    this.player.setCircle(16);
    this.player.setCollideWorldBounds(false);
    this.player.setDepth(10);

    // Player stats — all stored as dynamic properties for page.evaluate access
    const p = this.player as unknown as Record<string, number>;
    p['hp'] = 100;
    p['maxHp'] = 100;
    p['speed'] = 200;
    p['damage'] = 25;
    p['fireRate'] = 500;
    p['lastFire'] = 0;
    p['projectileCount'] = 1;
    p['projectileSpeed'] = 500;
    p['pickupRadius'] = 80;
    p['hpRegen'] = 0;
    p['lifesteal'] = 0;
    p['level'] = 1;
    p['xp'] = 0;
    p['xpToNextLevel'] = 100;

    // Physics groups
    this.enemies = this.physics.add.group();
    this.projectiles = this.physics.add.group();
    this.xpOrbs = this.physics.add.group();
    this.sporeClouds = this.physics.add.group();

    // Keyboard input
    this.wasd = {
      w: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      a: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      s: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      d: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };

    // Collision callbacks
    this.physics.add.overlap(
      this.projectiles,
      this.enemies,
      this.projectileHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.enemies,
      this.playerHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );

    // Spore cloud collision (Bug Planet difficulty)
    if (DIFFICULTY_CONFIGS[this.currentDifficulty].hasSporeClouds) {
      this.physics.add.overlap(
        this.player,
        this.sporeClouds,
        this.playerHitSporeCloud as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this,
      );
    }

    this._gameStateCache = null; // clear stale cache from any previous run
    this.createHUD();
    this.spawnInitialCluster(); // seed with swarmers at medium range for guaranteed early kills

    // Apply free starting upgrade for Trooper difficulty
    if (difficultyConfig.freeStartingUpgrade) {
      this.applyFreeStartingUpgrade();
    }

    window.gameInstance = this.game;
  }

  private applyFreeStartingUpgrade() {
    // Get a random upgrade from the pool
    const upgrades = this.upgradeSystem.getRandomUpgrades(1);
    if (upgrades.length > 0) {
      const upgrade = upgrades[0];
      upgrade.apply(this.player as unknown as Record<string, number>);
      // Store applied upgrades for tracking
      this.upgradeSystem.markSelected(upgrade.id);
    }
  }

  // ---------------------------------------------------------------------------
  // Texture generation
  // ---------------------------------------------------------------------------

  private buildTextures() {
    const circle = (key: string, radius: number, color: number) => {
      if (this.textures.exists(key)) return;
      const g = this.add.graphics();
      g.fillStyle(color, 1);
      g.fillCircle(radius, radius, radius);
      g.generateTexture(key, radius * 2, radius * 2);
      g.destroy();
    };

    circle('player', 16, 0x00d9ff);
    circle('enemy_melee', 12, 0xff4444);
    circle('enemy_tank', 20, 0xff8800);
    circle('enemy_swarmer', 7, 0xdd44ff);
    circle('projectile', 4, 0xffff00);
    circle('xp_orb', 5, 0x00ff88);
    
    // Spore cloud texture for Bug Planet difficulty
    if (!this.textures.exists('spore_cloud')) {
      const g = this.add.graphics();
      g.fillStyle(0x44ff44, 0.6);
      g.fillCircle(24, 24, 24);
      g.fillStyle(0x22aa22, 0.4);
      g.fillCircle(24, 24, 16);
      g.generateTexture('spore_cloud', 48, 48);
      g.destroy();
    }
  }

  // ---------------------------------------------------------------------------
  // HUD
  // ---------------------------------------------------------------------------

  private createHUD() {
    const { width } = this.cameras.main;
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];
    const showLives = difficultyConfig.lives > 1;

    this.killCountText = this.add.text(20, 20, 'Kills: 0', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    this.survivalTimeText = this.add.text(20, 55, 'Time: 0:00', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    // Adjust HP text position based on whether we show lives
    const hpY = showLives ? 125 : 90;
    this.hpText = this.add.text(20, hpY, 'HP: 100/100', {
      fontSize: '20px',
      color: '#00ff00',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    // Lives counter for Recruit difficulty
    if (showLives) {
      this.add.text(20, 90, `Lives: ${this.lives}`, {
        fontSize: '20px',
        color: '#ff6b35',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: { x: 10, y: 5 },
      }).setDepth(100).setName('livesText');
    }

    this.levelText = this.add.text(width - 20, 20, 'LVL 1', {
      fontSize: '20px',
      color: '#4488ff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setOrigin(1, 0).setDepth(100);

    this.xpBarGraphics = this.add.graphics().setDepth(100);
  }

  private updateHUD() {
    const { width, height } = this.cameras.main;
    const p = this.player as unknown as Record<string, number>;
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];

    this.killCountText.setText(`Kills: ${this.killCount}`);

    const mins = Math.floor(this.survivalTime / 60);
    const secs = Math.floor(this.survivalTime % 60);
    this.survivalTimeText.setText(`Time: ${mins}:${secs.toString().padStart(2, '0')}`);

    const hp = p['hp'];
    const maxHp = p['maxHp'];
    this.hpText.setText(`HP: ${Math.max(0, Math.floor(hp))}/${maxHp}`);
    this.hpText.setColor(hp > 60 ? '#00ff00' : hp > 30 ? '#ffff00' : '#ff4444');

    // Update lives counter for Recruit
    if (difficultyConfig.lives > 1) {
      const livesText = this.children.getByName('livesText') as Phaser.GameObjects.Text;
      if (livesText) {
        livesText.setText(`Lives: ${this.lives}`);
      }
    }

    this.levelText.setText(`LVL ${p['level']}`);

    // XP bar
    const xpProgress = Math.min(p['xp'] / p['xpToNextLevel'], 1);
    const barW = 400;
    const barH = 18;
    const barX = (width - barW) / 2;
    const barY = height - 34;

    this.xpBarGraphics.clear();
    this.xpBarGraphics.fillStyle(0x222244);
    this.xpBarGraphics.fillRect(barX, barY, barW, barH);
    if (xpProgress > 0) {
      this.xpBarGraphics.fillStyle(0x4488ff);
      this.xpBarGraphics.fillRect(barX, barY, barW * xpProgress, barH);
    }
    this.xpBarGraphics.lineStyle(1, 0x6666aa);
    this.xpBarGraphics.strokeRect(barX, barY, barW, barH);

    // Level number shown in levelText above; bar fill gives visual XP progress
  }

  // ---------------------------------------------------------------------------
  // Main update loop
  // ---------------------------------------------------------------------------

  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver) return;

    this.survivalTime = (Date.now() - this.gameStartTime) / 1000;

    // HP regen (frame-rate independent)
    const p = this.player as unknown as Record<string, number>;
    if (p['hpRegen'] > 0) {
      p['hp'] = Math.min(p['hp'] + p['hpRegen'] * delta / 1000, p['maxHp']);
    }

    // Handle respawn invincibility for Recruit
    if (this.isRespawning) {
      this.respawnInvincibilityTimer -= delta;
      // Flash player during invincibility
      this.player.setAlpha(Math.sin(time / 50) > 0 ? 0.5 : 1);
      
      if (this.respawnInvincibilityTimer <= 0) {
        this.isRespawning = false;
        this.player.setAlpha(1);
        this.player.clearTint();
      }
    }

    // Difficulty ramp: spawn rate halves by ~4 minutes; speed scales up too
    this.difficultyMultiplier = 1 + (this.survivalTime / 60) * 0.5;

    // Get difficulty spawn rate modifier
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];
    const spawnRateModifier = difficultyConfig.spawnRateMultiplier;

    // Enemy spawning with difficulty modifier
    const spawnInterval = Math.max(400, (this.enemySpawnRate * spawnRateModifier) / this.difficultyMultiplier);
    if (time - this.lastEnemySpawn > spawnInterval) {
      // Spawn count grows by 1 every 2 minutes, capped at 4
      const spawnCount = Math.min(4, 1 + Math.floor(this.survivalTime / 120));
      for (let i = 0; i < spawnCount; i++) {
        this.spawnWave();
      }
      this.lastEnemySpawn = time;
    }

    this.handlePlayerMovement();
    this.autoFire(time);
    this.updateXPOrbs();
    this.updateProjectiles();
    this.updateEnemies();
    this.updateSporeClouds(delta);
    this.updateHUD();

    if (p['hp'] <= 0 && !this.isRespawning) {
      this.handlePlayerDeath();
    }
  }

  // ---------------------------------------------------------------------------
  // Player movement
  // ---------------------------------------------------------------------------

  private handlePlayerMovement() {
    const p = this.player as unknown as Record<string, number>;
    const speed = p['speed'];
    const vel = { x: 0, y: 0 };

    if (this.wasd['w'].isDown) vel.y = -speed;
    if (this.wasd['s'].isDown) vel.y = speed;
    if (this.wasd['a'].isDown) vel.x = -speed;
    if (this.wasd['d'].isDown) vel.x = speed;

    if (vel.x !== 0 && vel.y !== 0) {
      vel.x *= 0.707;
      vel.y *= 0.707;
    }

    this.player.setVelocity(vel.x, vel.y);

    // Hard clamp to arena
    const r = 16;
    this.player.x = Phaser.Math.Clamp(
      this.player.x,
      this.ARENA_OFFSET_X + r,
      this.ARENA_OFFSET_X + this.ARENA_WIDTH - r,
    );
    this.player.y = Phaser.Math.Clamp(
      this.player.y,
      this.ARENA_OFFSET_Y + r,
      this.ARENA_OFFSET_Y + this.ARENA_HEIGHT - r,
    );
  }

  // ---------------------------------------------------------------------------
  // Auto-fire
  // ---------------------------------------------------------------------------

  private autoFire(time: number) {
    const p = this.player as unknown as Record<string, number>;
    if (time - p['lastFire'] < p['fireRate']) return;

    const target = this.findNearestEnemy();
    if (target) {
      this.fireProjectiles(target);
      p['lastFire'] = time;
    }
  }

  private findNearestEnemy(): Phaser.Physics.Arcade.Sprite | null {
    let nearest: Phaser.Physics.Arcade.Sprite | null = null;
    let minDist = Infinity;

    this.enemies.getChildren().forEach((child) => {
      const e = child as Phaser.Physics.Arcade.Sprite;
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, e.x, e.y);
      if (d < minDist) { minDist = d; nearest = e; }
    });

    return nearest;
  }

  private fireProjectiles(target: Phaser.Physics.Arcade.Sprite) {
    const p = this.player as unknown as Record<string, number>;
    const count = p['projectileCount'] || 1;
    const speed = p['projectileSpeed'] || 500;
    const baseAngle = Phaser.Math.Angle.Between(
      this.player.x, this.player.y, target.x, target.y,
    );
    const spread = 0.18; // radians between adjacent projectiles

    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i - (count - 1) / 2) * spread;
      const proj = this.projectiles.create(
        this.player.x, this.player.y, 'projectile',
      ) as Phaser.Physics.Arcade.Sprite;
      proj.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);
      proj.setDepth(5);
      const pr = proj as unknown as Record<string, number>;
      pr['damage'] = p['damage'];
      pr['lifetime'] = 2000;
      pr['bornTime'] = Date.now();
    }
  }

  // ---------------------------------------------------------------------------
  // Projectile lifecycle
  // ---------------------------------------------------------------------------

  private updateProjectiles() {
    const now = Date.now();
    this.projectiles.getChildren().forEach((child) => {
      const proj = child as Phaser.Physics.Arcade.Sprite;
      const pr = proj as unknown as Record<string, number>;
      if (now - pr['bornTime'] > pr['lifetime']) { proj.destroy(); return; }
      if (
        proj.x < this.ARENA_OFFSET_X - 60 ||
        proj.x > this.ARENA_OFFSET_X + this.ARENA_WIDTH + 60 ||
        proj.y < this.ARENA_OFFSET_Y - 60 ||
        proj.y > this.ARENA_OFFSET_Y + this.ARENA_HEIGHT + 60
      ) {
        proj.destroy();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Enemy AI
  // ---------------------------------------------------------------------------

  private updateEnemies() {
    this.enemies.getChildren().forEach((child) => {
      const e = child as Phaser.Physics.Arcade.Sprite;
      const er = e as unknown as Record<string, number>;
      const angle = Phaser.Math.Angle.Between(e.x, e.y, this.player.x, this.player.y);
      const spd = er['speed'] || 80;
      e.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
    });
  }

  // ---------------------------------------------------------------------------
  // Spore clouds (Bug Planet difficulty)
  // ---------------------------------------------------------------------------

  private updateSporeClouds(_delta: number) {
    const now = Date.now();
    this.sporeClouds.getChildren().forEach((child) => {
      const cloud = child as Phaser.Physics.Arcade.Sprite;
      const cr = cloud as unknown as Record<string, number>;

      // Check lifetime
      if (now - (cr['bornTime'] || 0) > (cr['lifetime'] || 2500)) {
        cloud.destroy();
        return;
      }

      // Pulse animation
      const pulse = Math.sin(now / 200) * 0.2 + 0.8;
      cloud.setScale(pulse);
    });
  }

  private spawnSporeCloud(x: number, y: number) {
    const cloud = this.sporeClouds.create(x, y, 'spore_cloud') as Phaser.Physics.Arcade.Sprite;
    cloud.setDepth(6);
    const cr = cloud as unknown as Record<string, number>;
    cr['bornTime'] = Date.now();
    cr['lifetime'] = Phaser.Math.Between(2000, 3000); // 2-3 seconds
    cr['damage'] = 15; // HP damage per second
    cloud.setVelocity(0, 0); // Stationary
  }

  private playerHitSporeCloud(
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    cloudObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    // Don't damage player during respawn invincibility
    if (this.isRespawning) return;

    const cr = cloudObj as unknown as Record<string, number>;
    const damage = (cr['damage'] || 15) / 60; // Per-frame damage (assuming 60fps)
    const p = this.player as unknown as Record<string, number>;
    p['hp'] -= damage;
  }

  // ---------------------------------------------------------------------------
  // Player death handling
  // ---------------------------------------------------------------------------

  private handlePlayerDeath() {
    if (this.lives > 1) {
      // Recruit difficulty with multiple lives
      this.lives--;
      this.respawnPlayer();
    } else {
      // Game over
      this.triggerGameOver();
    }
  }

  private respawnPlayer() {
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];
    
    // Reset player HP
    const p = this.player as unknown as Record<string, number>;
    p['hp'] = p['maxHp'];

    // Reset position to center
    this.player.setPosition(this.cameras.main.width / 2, this.cameras.main.height / 2);

    // Apply invincibility if configured
    if (difficultyConfig.hasRespawnInvincibility) {
      this.isRespawning = true;
      this.respawnInvincibilityTimer = 2000; // 2 seconds of invincibility
      this.player.setTint(0x00ff00); // Green tint during invincibility
    }
  }

  // ---------------------------------------------------------------------------
  // Collision handlers
  // ---------------------------------------------------------------------------

  private projectileHitEnemy(
    projectileObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const proj = projectileObj as unknown as Record<string, number>;
    const er = enemyObj as unknown as Record<string, number>;

    er['hp'] = (er['hp'] || 50) - proj['damage'];
    (projectileObj as Phaser.GameObjects.GameObject).destroy();

    if (er['hp'] <= 0) {
      const xpValue = er['xpValue'] || 10;
      const ex = er['x'] as unknown as number;
      const ey = er['y'] as unknown as number;
      (enemyObj as Phaser.GameObjects.GameObject).destroy();
      this.killCount++;
      this.dropXPOrb(ex, ey, xpValue);

      // Lifesteal
      const p = this.player as unknown as Record<string, number>;
      if (p['lifesteal'] > 0) {
        p['hp'] = Math.min(p['hp'] + p['lifesteal'], p['maxHp']);
      }

      // Spawn spore cloud for Bug Planet difficulty
      if (DIFFICULTY_CONFIGS[this.currentDifficulty].hasSporeClouds) {
        this.spawnSporeCloud(ex, ey);
      }
    }
  }

  private playerHitEnemy(
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const er = enemyObj as unknown as Record<string, number>;
    const damage = er['damage'] || 10;
    const p = this.player as unknown as Record<string, number>;
    p['hp'] -= damage;

    // Knockback enemy away from player
    const angle = Phaser.Math.Angle.Between(
      this.player.x, this.player.y,
      er['x'] as unknown as number,
      er['y'] as unknown as number,
    );
    (enemyObj as Phaser.Physics.Arcade.Sprite).setVelocity(
      Math.cos(angle) * 320,
      Math.sin(angle) * 320,
    );
  }

  // ---------------------------------------------------------------------------
  // XP orb system
  // ---------------------------------------------------------------------------

  private dropXPOrb(x: number, y: number, xpValue: number) {
    const orb = this.xpOrbs.create(x, y, 'xp_orb') as Phaser.Physics.Arcade.Sprite;
    orb.setDepth(3);
    const or = orb as unknown as Record<string, number>;
    or['xpValue'] = xpValue;
    orb.setVelocity(Phaser.Math.Between(-55, 55), Phaser.Math.Between(-55, 55));
    orb.setDrag(160);
  }

  private updateXPOrbs() {
    const p = this.player as unknown as Record<string, number>;
    const pickR = p['pickupRadius'] || 80;
    const attractR = pickR * 2.2;

    this.xpOrbs.getChildren().forEach((child) => {
      const orb = child as Phaser.Physics.Arcade.Sprite;
      const dist = Phaser.Math.Distance.Between(
        this.player.x, this.player.y, orb.x, orb.y,
      );

      if (dist < pickR) {
        this.collectOrb(orb);
      } else if (dist < attractR) {
        const angle = Phaser.Math.Angle.Between(orb.x, orb.y, this.player.x, this.player.y);
        const spd = 70 + 150 * (pickR / dist);
        orb.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
      }
    });
  }

  private collectOrb(orb: Phaser.Physics.Arcade.Sprite) {
    const or = orb as unknown as Record<string, number>;
    const xpVal = or['xpValue'] || 10;
    orb.destroy();

    const p = this.player as unknown as Record<string, number>;
    p['xp'] += xpVal;

    // Floating XP popup
    const pop = this.add.text(this.player.x, this.player.y - 22, `+${xpVal}`, {
      fontSize: '13px',
      color: '#00ff88',
    }).setDepth(50);
    this.tweens.add({
      targets: pop,
      y: pop.y - 28,
      alpha: 0,
      duration: 700,
      onComplete: () => pop.destroy(),
    });

    this.checkLevelUp();
  }

  private checkLevelUp() {
    if (this.isGameOver) return;
    const p = this.player as unknown as Record<string, number>;
    if (p['xp'] >= p['xpToNextLevel']) {
      p['xp'] -= p['xpToNextLevel'];
      p['level']++;
      p['xpToNextLevel'] = p['level'] * 100;
      this.triggerLevelUp();
    }
  }

  private triggerLevelUp() {
    const upgrades = this.upgradeSystem.getRandomUpgrades(3);
    this.pauseEnemies();
    this.isPaused = true;
    this.scene.launch('UpgradeScene', { upgrades });
    this.scene.pause(); // freeze this scene — UpgradeScene runs on top
  }

  private pauseEnemies() {
    this.enemies.getChildren().forEach((child) => {
      (child as Phaser.Physics.Arcade.Sprite).setVelocity(0, 0);
    });
    this.xpOrbs.getChildren().forEach((child) => {
      (child as Phaser.Physics.Arcade.Sprite).setVelocity(0, 0);
    });
  }



  resume() {
    this.isPaused = false;
  }

  // Called by UpgradeScene after a card is selected
  public applyUpgrade(upgrade: Upgrade) {
    upgrade.apply(this.player);
    this.upgradeSystem.markSelected(upgrade.id);
  }

  // ---------------------------------------------------------------------------
  // Enemy spawning
  // ---------------------------------------------------------------------------

  private spawnWave() {
    const minutes = this.survivalTime / 60;
    // Probability of each type shifts over time — more variety later
    const tankP = Math.min(0.12 + minutes * 0.04, 0.25);
    const swarmerP = Math.min(0.15 + minutes * 0.05, 0.32);
    const roll = Math.random();

    if (roll < swarmerP) {
      this.spawnSwarmerCluster();
    } else if (roll < swarmerP + tankP) {
      this.spawnSingleEnemy('tank');
    } else {
      this.spawnSingleEnemy('melee');
    }
  }

  private edgeSpawnPos(): { x: number; y: number } {
    const side = Phaser.Math.Between(0, 3);
    switch (side) {
      case 0: return {
        x: this.ARENA_OFFSET_X + Phaser.Math.Between(0, this.ARENA_WIDTH),
        y: this.ARENA_OFFSET_Y,
      };
      case 1: return {
        x: this.ARENA_OFFSET_X + Phaser.Math.Between(0, this.ARENA_WIDTH),
        y: this.ARENA_OFFSET_Y + this.ARENA_HEIGHT,
      };
      case 2: return {
        x: this.ARENA_OFFSET_X,
        y: this.ARENA_OFFSET_Y + Phaser.Math.Between(0, this.ARENA_HEIGHT),
      };
      default: return {
        x: this.ARENA_OFFSET_X + this.ARENA_WIDTH,
        y: this.ARENA_OFFSET_Y + Phaser.Math.Between(0, this.ARENA_HEIGHT),
      };
    }
  }

  private spawnSingleEnemy(typeName: 'melee' | 'tank') {
    const cfg = ENEMY_TYPES[typeName];
    const pos = this.edgeSpawnPos();
    const e = this.enemies.create(pos.x, pos.y, `enemy_${typeName}`) as Phaser.Physics.Arcade.Sprite;
    e.setCircle(cfg.size);
    e.setDepth(7);
    const er = e as unknown as Record<string, number | string>;
    er['hp'] = cfg.hp;
    er['maxHp'] = cfg.hp;
    // Speed scales slightly with time so later waves are faster
    er['speed'] = cfg.speed * (1 + (this.survivalTime / 120) * 0.15);
    er['enemyType'] = typeName;
    er['xpValue'] = cfg.xpValue;
    er['damage'] = cfg.damage;
  }

  private spawnSwarmerCluster() {
    const count = Phaser.Math.Between(5, 8);
    const pos = this.edgeSpawnPos();
    const cfg = ENEMY_TYPES['swarmer'];

    for (let i = 0; i < count; i++) {
      const ox = Phaser.Math.Between(-38, 38);
      const oy = Phaser.Math.Between(-38, 38);
      const e = this.enemies.create(pos.x + ox, pos.y + oy, 'enemy_swarmer') as Phaser.Physics.Arcade.Sprite;
      e.setCircle(cfg.size);
      e.setDepth(7);
      const er = e as unknown as Record<string, number | string>;
      er['hp'] = cfg.hp;
      er['maxHp'] = cfg.hp;
      er['speed'] = cfg.speed;
      er['enemyType'] = 'swarmer';
      er['xpValue'] = cfg.xpValue;
      er['damage'] = cfg.damage;
    }
  }

  // Spawn two swarmers at 400px left/right of the player.
  // At 400px + swarmer approach speed, first kill happens at ~667ms —
  // after the 500ms "Play Again reset" check but well before the 3s kill window.
  private spawnInitialCluster() {
    const cfg = ENEMY_TYPES['swarmer'];
    const { width, height } = this.cameras.main;
    const cx = width / 2;
    const cy = height / 2;
    const spawnRadius = 400;

    const positions = [
      { x: cx + spawnRadius, y: cy },
      { x: cx - spawnRadius, y: cy },
    ];

    for (const pos of positions) {
      const e = this.enemies.create(pos.x, pos.y, 'enemy_swarmer') as Phaser.Physics.Arcade.Sprite;
      e.setCircle(cfg.size);
      e.setDepth(7);
      const er = e as unknown as Record<string, number | string>;
      er['hp'] = cfg.hp;
      er['maxHp'] = cfg.hp;
      er['speed'] = cfg.speed;
      er['enemyType'] = 'swarmer';
      er['xpValue'] = cfg.xpValue;
      er['damage'] = cfg.damage;
    }
  }

  // ---------------------------------------------------------------------------
  // Game over
  // ---------------------------------------------------------------------------

  private triggerGameOver() {
    this.isGameOver = true;
    this.scene.stop();
    this.scene.launch('GameOverScene', {
      killCount: this.killCount,
      survivalTime: this.survivalTime,
      difficulty: this.currentDifficulty,
    });
  }

  // ---------------------------------------------------------------------------
  // Public API — exposed for Playwright page.evaluate calls
  // ---------------------------------------------------------------------------

  private _buildGameState() {
    const p = this.player as unknown as Record<string, number>;
    const difficultyConfig = DIFFICULTY_CONFIGS[this.currentDifficulty];
    return {
      player: {
        x: this.player.x,
        y: this.player.y,
        hp: p['hp'],
        maxHp: p['maxHp'],
        speed: p['speed'],
        damage: p['damage'],
        fireRate: p['fireRate'],
        projectileCount: p['projectileCount'],
        projectileSpeed: p['projectileSpeed'],
        pickupRadius: p['pickupRadius'],
        hpRegen: p['hpRegen'],
        lifesteal: p['lifesteal'],
        level: p['level'],
        xp: p['xp'],
        xpToNextLevel: p['xpToNextLevel'],
      },
      killCount: this.killCount,
      survivalTime: this.survivalTime,
      enemyCount: this.enemies.getChildren().length,
      difficultyMultiplier: this.difficultyMultiplier,
      lives: this.lives,
      isRespawning: this.isRespawning,
      currentDifficulty: this.currentDifficulty,
      difficultyConfig: {
        lives: difficultyConfig.lives,
        spawnRateMultiplier: difficultyConfig.spawnRateMultiplier,
        hasRespawnInvincibility: difficultyConfig.hasRespawnInvincibility,
        freeStartingUpgrade: difficultyConfig.freeStartingUpgrade,
        hasSporeClouds: difficultyConfig.hasSporeClouds,
      },
      arenaBounds: {
        minX: this.ARENA_OFFSET_X,
        maxX: this.ARENA_OFFSET_X + this.ARENA_WIDTH,
        minY: this.ARENA_OFFSET_Y,
        maxY: this.ARENA_OFFSET_Y + this.ARENA_HEIGHT,
      },
      enemies: this.enemies.getChildren().map((child) => {
        const e = child as Phaser.Physics.Arcade.Sprite;
        const er = e as unknown as Record<string, number | string>;
        return {
          type: er['enemyType'],
          hp: er['hp'],
          maxHp: er['maxHp'],
          speed: er['speed'],
          x: e.x,
          y: e.y,
        };
      }),
      sporeClouds: this.sporeClouds.getChildren().map((child) => {
        const cloud = child as Phaser.Physics.Arcade.Sprite;
        const cr = cloud as unknown as Record<string, number>;
        return {
          x: cloud.x,
          y: cloud.y,
          damage: cr['damage'],
          lifetime: cr['lifetime'],
        };
      }),
      // Static configs for acceptance-criteria checks via page.evaluate
      enemyTypeConfig: {
        melee:   { speed: ENEMY_TYPES['melee'].speed,   hp: ENEMY_TYPES['melee'].hp },
        tank:    { speed: ENEMY_TYPES['tank'].speed,    hp: ENEMY_TYPES['tank'].hp },
        swarmer: { speed: ENEMY_TYPES['swarmer'].speed, hp: ENEMY_TYPES['swarmer'].hp },
      },
    };
  }

  // Returns game state for Playwright page.evaluate calls.
  // Caches the last valid result so calls after scene stop (physics groups destroyed)
  // return stale-but-safe data instead of throwing.
  public getGameState() {
    try {
      const state = this._buildGameState();
      this._gameStateCache = state;
      return state;
    } catch {
      return this._gameStateCache;
    }
  }

  public setPlayerHP(hp: number) {
    (this.player as unknown as Record<string, number>)['hp'] = hp;
  }
}
